import { OwnersContext, OwnersContextType } from '@/contexts/OwnersContext'
import {
  Badge,
  Box,
  Button,
  HStack,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Skeleton,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react'
import { ChevronLeft, ChevronRight, Mail, MoreVertical } from 'lucide-react'
import { useContext } from 'react'
import { FiEdit2, FiTrash2 } from 'react-icons/fi'

export function OwnerListSimple() {
  const {
    data: owners,
    count,
    isLoading,
    totalPages,
    currentPage,
    filters,
    isSendingVerification,
    setFilters,
    handleSendVerification,
    handleRowClick,
    handleFormOpen,
    handleDelete,
  } = useContext(OwnersContext) as OwnersContextType

  const startItem = (currentPage - 1) * (filters?.limit || 12) + 1
  const endItem = Math.min(currentPage * (filters?.limit || 12), owners.length)

  const handlePageChange = (newPage: number) => {
    setFilters((prevState) => ({ ...prevState, page: newPage }))
  }

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setFilters((prevState) => ({ ...prevState, page: currentPage - 1 }))
    }
  }

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setFilters((prevState) => ({ ...prevState, page: currentPage + 1 }))
    }
  }
  if (isLoading) {
    return (
      <Box bg="white" borderRadius="lg" border="1px" borderColor="gray.200">
        <Box overflowX="auto">
          <Table variant="simple">
            <Thead bg="gray.50">
              <Tr>
                <Th width="40%" minW="200px">
                  Nome
                </Th>
                <Th width="30%" minW="180px">
                  Email
                </Th>
                <Th width="15%" minW="100px" textAlign="center">
                  Propriedades
                </Th>
                <Th width="15%" minW="120px" textAlign="center">
                  Status Email
                </Th>
                <Th width="50px"></Th>
              </Tr>
            </Thead>
            <Tbody>
              {Array.from({ length: 12 }).map((_, i) => (
                <Tr key={i}>
                  <Td>
                    <Skeleton height="20px" />
                  </Td>
                  <Td>
                    <Skeleton height="20px" />
                  </Td>
                  <Td>
                    <Skeleton height="20px" />
                  </Td>
                  <Td>
                    <Skeleton height="20px" />
                  </Td>
                  <Td>
                    <Skeleton height="20px" />
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
      </Box>
    )
  }

  if (!owners.length) {
    return (
      <Box
        bg="white"
        borderRadius="lg"
        border="1px"
        borderColor="gray.200"
        p={8}
        textAlign="center"
      >
        <Text color="gray.500" fontSize="lg" mb={2}>
          Nenhum proprietário encontrado
        </Text>
        <Text color="gray.400" fontSize="sm">
          Tente ajustar os filtros de busca
        </Text>
      </Box>
    )
  }

  return (
    <Box bg="white" borderRadius="lg" border="1px" borderColor="gray.200">
      <Box overflowX="auto">
        <Table variant="simple">
          <Thead bg="gray.50">
            <Tr>
              <Th width="40%" minW="200px">
                Nome
              </Th>
              <Th width="30%" minW="180px">
                Email
              </Th>
              <Th width="15%" minW="100px" textAlign="center">
                Propriedades
              </Th>
              <Th width="15%" minW="120px" textAlign="center">
                Status Email
              </Th>
              <Th width="50px"></Th>
            </Tr>
          </Thead>
          <Tbody>
            {owners.map((owner) => (
              <Tr
                key={owner.id}
                _hover={{ bg: 'gray.50' }}
                cursor="pointer"
                onClick={() => handleRowClick(owner)}
              >
                <Td>
                  <Text fontWeight="medium" color="gray.900">
                    {owner.name || 'N/A'}
                  </Text>
                </Td>
                <Td>
                  <Text color="gray.600" fontSize="sm">
                    {owner.email || 'N/A'}
                  </Text>
                </Td>
                <Td textAlign="center">
                  <Badge
                    colorScheme={
                      owner.property_count && owner.property_count > 0
                        ? 'blue'
                        : 'gray'
                    }
                    px={2}
                    py={1}
                    borderRadius="md"
                    fontSize="xs"
                  >
                    {owner.property_count || 0}
                  </Badge>
                </Td>
                <Td textAlign="center">
                  <Badge
                    colorScheme={owner.email_confirmed_at ? 'green' : 'red'}
                    px={2}
                    py={1}
                    borderRadius="md"
                    fontSize="xs"
                  >
                    {owner.email_confirmed_at ? 'Verificado' : 'Pendente'}
                  </Badge>
                </Td>
                <Td onClick={(e) => e.stopPropagation()}>
                  <Menu>
                    <MenuButton
                      as={IconButton}
                      icon={<MoreVertical size={16} />}
                      variant="ghost"
                      size="sm"
                    />
                    <MenuList>
                      <MenuItem
                        icon={<FiEdit2 />}
                        onClick={() => handleFormOpen(owner)}
                      >
                        Editar
                      </MenuItem>
                      {!owner.email_confirmed_at && owner.email && (
                        <MenuItem
                          icon={<Mail size={16} />}
                          onClick={() => handleSendVerification(owner.email!)}
                          isDisabled={isSendingVerification}
                        >
                          {isSendingVerification
                            ? 'Enviando...'
                            : 'Enviar Verificação'}
                        </MenuItem>
                      )}
                      <MenuItem
                        icon={<FiTrash2 />}
                        color="red.500"
                        onClick={() => handleDelete(owner.id)}
                      >
                        Excluir
                      </MenuItem>
                    </MenuList>
                  </Menu>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>

      {/* Pagination Controls - Always show for testing */}
      {owners.length > 0 && (
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          p={4}
          borderTop="1px"
          borderColor="gray.200"
          bg="white"
          borderBottomRadius="lg"
        >
          {/* Results Info */}
          <Text fontSize="sm" color="gray.600">
            Exibindo {startItem}-{endItem} de {count} proprietários
          </Text>

          {/* Pagination Buttons */}
          <HStack spacing={2}>
            <IconButton
              aria-label="Página anterior"
              icon={<ChevronLeft size={16} />}
              size="sm"
              variant="outline"
              isDisabled={currentPage === 1}
              onClick={handlePrevPage}
            />

            {/* Page Numbers */}
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let pageNum
              if (totalPages <= 5) {
                pageNum = i + 1
              } else if (currentPage <= 3) {
                pageNum = i + 1
              } else if (currentPage >= totalPages - 2) {
                pageNum = totalPages - 4 + i
              } else {
                pageNum = currentPage - 2 + i
              }

              return (
                <Button
                  key={pageNum}
                  size="sm"
                  variant={currentPage === pageNum ? 'solid' : 'outline'}
                  colorScheme={currentPage === pageNum ? 'blue' : 'gray'}
                  onClick={() => handlePageChange(pageNum)}
                >
                  {pageNum}
                </Button>
              )
            })}

            <IconButton
              aria-label="Próxima página"
              icon={<ChevronRight size={16} />}
              size="sm"
              variant="outline"
              isDisabled={currentPage === totalPages}
              onClick={handleNextPage}
            />
          </HStack>
        </Box>
      )}
    </Box>
  )
}
